import { useMutation } from '@tanstack/react-query'
import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { SERVICE_DATE_FORMAT } from 'app/constants/date'
import { v4 as uuidv4 } from 'uuid'
import { TaskType } from 'app/constants/taskType'
import { startTrackedTask } from '@/modules/shared/utils/startTrackedTask'

/**
 * Hook for generating CSV exports
 */
export function useGenerateCsv(companyId: string) {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { t } = useLocalize()

  return useMutation({
    mutationFn: async ({
      reportType,
      drivers,
      date,
    }: {
      reportType: number
      drivers: Array<string>
      date: Moment
    }) => {
      const stopLoading = startLoading()
      try {
        const formattedDate = moment(date).format(SERVICE_DATE_FORMAT)
        const driverNumbers = drivers
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id))

        // Eksport nie liczy sie juz w tym zadaniu HTTP - serwer zaklada zadanie dla
        // FileProcessora i oddaje jego ID. Powod: Ocelot ma dla /api/* timeout 120 s,
        // wiec dluzszy eksport urywal sie na bramie mimo ze robota trwala dalej.
        // Gotowy plik pobiera TaskStatus z wyniku zadania, tak samo jak wydruki.
        const taskId = uuidv4()
        await startTrackedTask({
          companyId,
          type: TaskType.Export,
          taskId,
          start: () =>
            axiosInstance.post(`api/${companyId}/export/generate`, {
              id: taskId,
              drivers: driverNumbers,
              exportType: reportType,
              date: formattedDate,
            }),
        })

        return { taskId }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
        throw new Error(errorMessage)
      } finally {
        stopLoading()
      }
    },
    onSuccess: () => {
      toast.success({
        text: t('export.csv-generated-successfully'),
      })
    },
    onError: (error: Error) => {
      toast.error({
        text: t('errorCodes.genetaring-csv-failed'),
        content: error.message,
      })
    },
  })
}

/**
 * Hook for IMI export
 */
export function useImiExport(companyId: string) {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { t } = useLocalize()

  return useMutation({
    mutationFn: async ({
      importId,
      options,
      taskId,
    }: {
      importId: string
      options: any
      taskId: string
    }) => {
      const stopLoading = startLoading()
      try {
        const response = await axiosInstance.post(
          `api/${companyId}/export/imiExport`,
          {
            options: JSON.stringify(options),
            importId,
            taskId,
          },
        )

        return response.data
      } catch (error: any) {
        const errorCode =
          error.response?.status === 403
            ? 'errorCode.UNAUTHORIZED'
            : error.response?.data?.code || 'UNKNOWN_ERROR'
        throw new Error(errorCode)
      } finally {
        stopLoading()
      }
    },
    onSuccess: () => {
      toast.success({
        text: t('imiExport.validation-success'),
        content: t('imiExport.validation-success-details'),
      })
    },
    onError: (error: Error) => {
      toast.error({
        text: t('import.upload-error'),
        content: error.message,
      })
    },
  })
}

/**
 * Helper to generate task ID
 */
export function useTaskId() {
  return () => uuidv4()
}

